/**
 * Payment helpers — pure, mode-agnostic utilities.
 *
 * Shared by:
 *   - `payment-api-mock.ts` (deterministic in-memory provider)
 *   - `payment-api-http.ts` (live `bizClient` round-trip)
 *   - `payment-api.ts` (mock↔live dispatcher / re-export hub)
 *
 * Nothing here issues network calls or touches state — every function is a
 * pure transformation safe to use in render paths, validators, and tests.
 *
 * Boundary: see `features/payment/types.ts` § boundary. Card PAN/CVV stays
 * in this layer; nothing in this file logs, persists, or echoes them.
 *
 * Backed by ADR-109 (Business Payment & Tender Engine).
 */

import type { CashierPaymentMethod } from '../cashier/types';
import type { CardBrand, CashTenderCalculation, CashTenderInput } from './types';

// ============================================================================
// Card helpers (ephemeral — never logged)
// ============================================================================

export function normalizeCardNumber(raw: string): string {
  return raw.replace(/\D/g, '');
}

export function last4(raw: string): string {
  const digits = normalizeCardNumber(raw);
  return digits.slice(-4).padStart(4, '\u2022');
}

export function detectCardBrand(raw: string): CardBrand {
  const digits = normalizeCardNumber(raw);
  if (digits.length === 0) return 'unknown';
  if (digits.startsWith('4')) return 'visa';
  if (/^(5[1-5]|2[2-7])/.test(digits)) return 'mastercard';
  if (/^3[47]/.test(digits)) return 'amex';
  if (/^(9792|65|979200|979210)/.test(digits)) return 'troy';
  return 'unknown';
}

export function formatCardNumber(raw: string, brand: CardBrand): string {
  const digits = normalizeCardNumber(raw).slice(0, brand === 'amex' ? 15 : 19);
  const groups = brand === 'amex' ? [4, 6, 5] : [4, 4, 4, 4, 3];
  const out: string[] = [];
  let cursor = 0;
  for (const size of groups) {
    if (cursor >= digits.length) break;
    out.push(digits.slice(cursor, cursor + size));
    cursor += size;
  }
  return out.join(' ');
}

/**
 * Luhn mod-10 checksum. Real providers enforce this too; we keep it client
 * side to reject obviously-invalid PANs before simulating the network call.
 */
export function isValidLuhn(raw: string): boolean {
  const digits = normalizeCardNumber(raw);
  if (digits.length < 12) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = Number.parseInt(digits[i]!, 10);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

export function isExpiryValid(month: number, year: number): boolean {
  if (!Number.isFinite(month) || !Number.isFinite(year)) return false;
  if (month < 1 || month > 12) return false;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (year < currentYear) return false;
  if (year === currentYear && month < currentMonth) return false;
  return true;
}

// ============================================================================
// Cash helpers
// ============================================================================

export function computeCashTender(input: CashTenderInput): CashTenderCalculation {
  const safeTotal = Math.max(0, input.totalMinor);
  const safeTendered = Math.max(0, input.tenderedMinor);
  const change = Math.max(0, safeTendered - safeTotal);
  return {
    totalMinor: safeTotal,
    tenderedMinor: safeTendered,
    changeMinor: change,
    sufficient: safeTendered >= safeTotal,
  };
}

// ============================================================================
// Tender composition
// ============================================================================

export function buildIdempotencyKey(
  orderId: string,
  method: CashierPaymentMethod,
  amountMinor: number,
  seed: string,
): string {
  return `${orderId}:${method}:${amountMinor}:${seed}`;
}

// ============================================================================
// Internal id helpers (also used by the mock provider for deterministic ids)
// ============================================================================

export function newClientId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
}
